<template>
  <div>
    <app-alert
      color="success"
      icon-pack="feather"
      icon="icon-mail"
      :active="!invalidCodeError"
      type="default">
      <p class="pt-2">{{ $t('VerificationAlertMsg') }}
        <a
          href="#"
          class="ml-0"
          @click.prevent="resendVerificationEmail()">
          {{ $t('ResendNewVerificationEmail') | lowercase }}
        </a>.
      </p>
    </app-alert>
    <app-alert
      color="warning"
      icon-pack="feather"
      icon="icon-alert-triangle"
      :active="!!invalidCodeError"
      type="default">
      <p class="pt-2">{{ $t('EnteredCodeInvalidAlertMsg') }}
        <a
          href="#"
          class="ml-0"
          @click.prevent="resendVerificationEmail()">
          {{ $t('ResendNewVerificationEmail') | lowercase }}
        </a>.
      </p>
    </app-alert>
    <form @submit.prevent>
      <div class="vx-row mb-6 mt-6">
        <div class="vx-col w-full">
          <vs-input
            v-model="verificationCode"
            class="w-full required"
            :label="$t('VerificationCode')"
            :name="$t('VerificationCode')"
            v-validate="'required|digits:6'"
            data-vv-validate-on="blur|input"
            :danger="errors.has($t('VerificationCode')) || !!invalidCodeError"
            :danger-text="errors.first($t('VerificationCode')) || invalidCodeError"
            val-icon-danger="clear"
            @input="invalidCodeError = ''"/>

          <small class="font-black"><i>{{ $t('DisableAlertMsg') }}.</i></small>

        </div>
      </div>

      <base-form-footer-action-buttons
        @save="disableTwoFactorAuthentication()"
        @cancel="$emit('close')">
        <template v-slot:save>
          {{ $t('$General.Disable')}}
        </template>
      </base-form-footer-action-buttons>
    </form>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

// Components
import BaseFormFooterActionButtons from '@/views/modules/_components/BaseFormFooterActionButtons.vue';
import AppAlert from '@/views/_components/AppAlert.vue';

// Services
import userService from '@/api/modules/user.service';

/**
 * Component to verify an email account
 *
 * @module src/views/modules/user/user-account/user-account-profile/UserAccountProfileSecurity2FADisable
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 */
export default {
  name: 'UserAccountProfileSecurity2FADisable',
  i18n: {
    messages: {
      en: {
        VerificationAlertMsg: 'We have sent you a verification email with a code. Please enter the code below. If you have not received the email, you can',
        EnteredCodeInvalidAlertMsg: 'Make sure the code is correct. If you have not received the email, you can',
        ResendNewVerificationEmail: 'Resend here',
        VerificationCode: 'Verification code',
        TwoFactorAuthDisabledNotifyTitle: 'Two-factor authentication',
        TwoFactorAuthDisabledNotifyMsg: 'Two-factor authentication disabled successfully',
        $ResendVerificationEmailNotify: {
          Title: 'Email sent',
          Msg: 'Verification email sent successfully',
        },
        DisableAlertMsg: 'If you disable two‑factor authentication, you\'ll need either your email and password or your Google account to sign in to GrapePerks.',
      },
    },
  },
  components: {
    BaseFormFooterActionButtons,
    AppAlert,
  },
  props: {

  },
  data() {
    return {
      verificationCode: '',
      invalidCodeError: '',
    };
  },
  computed: {

  },
  methods: {
    ...mapActions({
      disableTwoFactorAuthenticationByEmailCode: 'auth/disableTwoFactorAuthenticationByEmailCode',
    }),
    async resendVerificationEmail() {
      this.$vs.loading({ type: 'radius' });
      await userService.sentEmailCodeToDisabled2FA();
      this.$vs.loading.close();

      this.verificationCode = '';
      this.invalidCodeError = '';

      this.$showSuccessActionNotification({
        title: this.$t('$ResendVerificationEmailNotify.Title'),
        text: this.$t('$ResendVerificationEmailNotify.Msg'),
      });

      await this.$validator.reset();
    },
    async disableTwoFactorAuthentication() {
      const formIsValid = await this.$validator.validate();

      if (!formIsValid) {
        return;
      }

      try {
        this.$vs.loading({ type: 'radius' });
        await this.disableTwoFactorAuthenticationByEmailCode(this.verificationCode);

        this.$showSuccessActionNotification({
          title: this.$t('TwoFactorAuthDisabledNotifyTitle'),
          text: this.$t('TwoFactorAuthDisabledNotifyMsg'),
        });

        this.$emit('close');
      } catch (err) {
        if (err.response.data.statusCode === 400 && err.response.data.invalidCode) {
          this.invalidCodeError = err.response.data.message;
        }
      } finally {
        this.$vs.loading.close();
      }
    },
  },
};
</script>
